/**
 * One input a population is built from — a file in the data room, or a live source connector. A
 * population can combine several of these (mixing files and connectors); how they're mapped into the
 * target schema is described by the population-level `prompt`.
 */
export type PopulationSource =
  | {
      type: "file";
      id: string;
      label: string;
      /** Human-readable file type, e.g. "Excel spreadsheet" / "PDF document". Generic for now. */
      fileType: string;
      size: string;
      uploadedBy: string;
      uploadedAt: string;
    }
  | {
      type: "connector";
      id: string;
      /** The connector platform, e.g. "CSV export". */
      connectorType: string;
      /** The specific connection instance — distinguishes multiple connectors of the same type. */
      name: string;
      status: string;
      lastSync: string;
    };

/** The data type of a population field (its target schema type). */
export type FieldType = "text" | "date" | "number" | "currency" | "boolean";

/**
 * A column in a population's target schema. `numeric` drives right-alignment in the data
 * table (lines up with number/currency types); `isKey` marks the identifying field; `hint` is an
 * optional natural-language instruction telling the agent how to extract that field.
 */

export type RecordColumn = {
  key: string;
  label: string;
  type: FieldType;
  numeric?: boolean;
  isKey?: boolean;
  hint?: string;
};

/**
 * The row a sampled item draws on. The fixture population holds far fewer rows than a real sample
 * draws, so rows are cycled to fill it — but two items in one sample must never carry the same
 * identifier, or the same reference appears twice with different verdicts. Beyond the fixture, the
 * identifying column is re-derived past the highest value the fixture uses.
 */
export const sampledRow = (
  rows: Record<string, string>[],
  schema: RecordColumn[],
  index: number,
): Record<string, string> => {
  if (rows.length === 0) return {};
  const base = rows[index % rows.length];
  if (index < rows.length) return base;

  const keyColumn = schema.find(c => c.isKey) ?? schema[0];
  if (!keyColumn) return base;

  const values = rows.map(r => r[keyColumn.key]).filter(Boolean);
  const shape = /^(.*?)(\d+)$/.exec(values[0] ?? "");
  if (!shape) return {...base, [keyColumn.key]: `${values[0] ?? "Item"} ${index + 1}`};

  const [, prefix, digits] = shape;
  const highest = Math.max(...values.map(v => Number(/(\d+)$/.exec(v)?.[1] ?? 0)));
  const next = highest + 1 + (index - rows.length);
  return {...base, [keyColumn.key]: prefix + String(next).padStart(digits.length, "0")};
};

export type PopulationRow = {
  /** URL slug used to route to the population detail page. */
  id: string;
  name: string;
  /** The inputs this population is built from — one or more files and/or connectors. */
  sources: PopulationSource[];
  /** Population-level agentic instruction: how the sources are mapped into the target schema. */
  prompt: string;
  /** A short, human-readable description of what this population contains. */
  description: string;
  records: string;
  usedIn: string;
  lastRefreshed: string;
  /** When locked, the population is frozen — its records and schema can't be edited. */
  locked?: boolean;
  /** The population's target schema — its own typed fields. */
  schema: RecordColumn[];
  /** A sample of the population's records, keyed by schema field. */
  rows: Record<string, string>[];
};

// Dummy data — this is a UI prototype, nothing is wired up.
export const POPULATIONS: PopulationRow[] = [
  {
    id: "gl-journal-entries",
    name: "GL journal entries",
    sources: [
      {
        type: "file",
        id: "src-gl-export",
        label: "GL_Export_FY24.xlsx",
        fileType: "Excel spreadsheet",
        size: "2.4 MB",
        uploadedBy: "A. Okafor",
        uploadedAt: "12 Mar 2025",
      },
      {
        type: "connector",
        id: "src-gl-xero",
        connectorType: "CSV export",
        name: "GL_Northwind_UK_FY24.csv",
        status: "Connected",
        lastSync: "5 hours ago",
      },
      {
        type: "file",
        id: "src-gl-adjustments",
        label: "Adjustments_Q4.xlsx",
        fileType: "Excel spreadsheet",
        size: "412 KB",
        uploadedBy: "R. Mehta",
        uploadedAt: "14 Mar 2025",
      },
      {
        type: "connector",
        id: "src-gl-netsuite",
        connectorType: "CSV export",
        name: "GL_Northwind_US_FY24.csv",
        status: "Connected",
        lastSync: "1 day ago",
      },
      {
        type: "file",
        id: "src-gl-accruals",
        label: "Manual_Accruals.csv",
        fileType: "CSV file",
        size: "22 KB",
        uploadedBy: "M. Lindqvist",
        uploadedAt: "16 Mar 2025",
      },
    ],
    prompt:
      "Combine the GL export spreadsheets, the manual adjustment and accrual files, and the two ledger " +
      "CSV exports into one record per journal line — posting date, document reference, GL " +
      "account, narrative, debit and credit amounts, and the posting user. De-duplicate lines present " +
      "in multiple sources and ignore subtotal and opening-balance rows.",
    description: "All journal entries posted to the general ledger during FY2024.",
    records: "12,480",
    usedIn: "1 sample",
    lastRefreshed: "2 days ago",
    locked: true,
    schema: [
      {key: "date", label: "Date", type: "date", hint: "Use the posting date, not the document or entry date."},
      {key: "reference", label: "Reference", type: "text", isKey: true},
      {
        key: "account",
        label: "Account",
        type: "text",
        hint: "Capture the full GL account code and name, e.g. '6000 · Cost of sales'.",
      },
      {key: "description", label: "Description", type: "text"},
      {key: "debit", label: "Debit", type: "currency", numeric: true},
      {key: "credit", label: "Credit", type: "currency", numeric: true},
      {key: "postedBy", label: "Posted by", type: "text"},
    ],
    rows: [
      {
        date: "02 Apr 2024",
        reference: "JNL-100482",
        account: "6000 · Cost of sales",
        description: "Stock purchase — Q1 reconciliation",
        debit: "12,400.00",
        credit: "—",
        postedBy: "A. Okafor",
      },
      {
        date: "05 Apr 2024",
        reference: "JNL-100483",
        account: "2100 · Accounts payable",
        description: "Supplier invoice — Northwind Ltd",
        debit: "—",
        credit: "12,400.00",
        postedBy: "A. Okafor",
      },
      {
        date: "11 Apr 2024",
        reference: "JNL-100501",
        account: "4000 · Revenue",
        description: "Sales invoice — Contoso plc",
        debit: "—",
        credit: "48,250.00",
        postedBy: "M. Lindqvist",
      },
      {
        date: "18 Apr 2024",
        reference: "JNL-100545",
        account: "7200 · Payroll",
        description: "April salaries run",
        debit: "86,910.32",
        credit: "—",
        postedBy: "System",
      },
      {
        date: "24 Apr 2024",
        reference: "JNL-100588",
        account: "6500 · Marketing",
        description: "Conference sponsorship — FY24",
        debit: "9,000.00",
        credit: "—",
        postedBy: "R. Mehta",
      },
      {
        date: "30 Apr 2024",
        reference: "JNL-100604",
        account: "8100 · Depreciation",
        description: "Monthly depreciation — fixed assets",
        debit: "3,275.50",
        credit: "—",
        postedBy: "System",
      },
    ],
  },
  {
    id: "vendor-master-list",
    name: "Vendor master list",
    sources: [
      {
        type: "connector",
        id: "src-vendor-xero",
        connectorType: "CSV export",
        name: "GL_Northwind_UK_FY24.csv",
        status: "Connected",
        lastSync: "5 hours ago",
      },
    ],
    prompt:
      "Retrieve all supplier contacts from the vendor master export as at 31 March 2025, including contact name, " +
      "account number, tax registration, default currency, and bank account details. " +
      "Include both active and inactive suppliers, but exclude archived contacts.",
    description: "Active and inactive suppliers from the finance system, as at 31 March 2025.",
    records: "1,204",
    usedIn: "1 sample",
    lastRefreshed: "5 hours ago",
    schema: [
      {key: "vendor", label: "Vendor", type: "text"},
      {key: "accountNo", label: "Account no.", type: "text", isKey: true},
      {key: "taxRef", label: "Tax registration", type: "text"},
      {key: "currency", label: "Currency", type: "text"},
      {key: "bankAccount", label: "Bank account", type: "text"},
      {key: "status", label: "Status", type: "text"},
    ],
    rows: [
      {
        vendor: "Contoso Ltd",
        accountNo: "V-1001",
        taxRef: "GB123456789",
        currency: "GBP",
        bankAccount: "····4321",
        status: "Active",
      },
      {
        vendor: "Fabrikam Inc",
        accountNo: "V-1002",
        taxRef: "US98-7654321",
        currency: "USD",
        bankAccount: "····8876",
        status: "Active",
      },
      {
        vendor: "Tailspin Toys",
        accountNo: "V-1003",
        taxRef: "GB223344556",
        currency: "GBP",
        bankAccount: "····1190",
        status: "Inactive",
      },
      {
        vendor: "Northwind Traders",
        accountNo: "V-1004",
        taxRef: "GB778899001",
        currency: "EUR",
        bankAccount: "····2025",
        status: "Active",
      },
      {
        vendor: "Adventure Works",
        accountNo: "V-1005",
        taxRef: "US12-3456789",
        currency: "USD",
        bankAccount: "····5567",
        status: "Active",
      },
    ],
  },
  {
    id: "new-joiners-leavers",
    name: "New joiners & leavers",
    sources: [
      {
        type: "file",
        id: "src-hr-movements",
        label: "HR_Movements_Q1.csv",
        fileType: "CSV file",
        size: "84 KB",
        uploadedBy: "S. Patel",
        uploadedAt: "04 Apr 2025",
      },
      {
        type: "connector",
        id: "src-hr-bamboo",
        connectorType: "BambooHR",
        name: "HR_Movements_Q2.csv",
        status: "Connected",
        lastSync: "Yesterday",
      },
    ],
    prompt:
      "Combine the HR movements CSV with the BambooHR feed into one record per employee movement — " +
      "effective date, employee reference, department, movement type (joiner or leaver), and " +
      "approver. Prefer BambooHR where the two disagree, and exclude rows with a blank movement type.",
    description: "Employee joiners and leavers recorded during Q1 2025.",
    records: "318",
    usedIn: "1 sample",
    lastRefreshed: "1 week ago",
    locked: true,
    schema: [
      {key: "effectiveDate", label: "Effective date", type: "date"},
      {key: "employeeId", label: "Employee ID", type: "text", isKey: true},
      {key: "department", label: "Department", type: "text"},
      {key: "movement", label: "Movement", type: "text"},
      {key: "approver", label: "Approver", type: "text"},
    ],
    rows: [
      {
        effectiveDate: "08 Jan 2025",
        employeeId: "E-2041",
        department: "Finance",
        movement: "Joiner",
        approver: "S. Patel",
      },
      {
        effectiveDate: "15 Jan 2025",
        employeeId: "E-1888",
        department: "Sales",
        movement: "Leaver",
        approver: "S. Patel",
      },
      {
        effectiveDate: "01 Feb 2025",
        employeeId: "E-2050",
        department: "Engineering",
        movement: "Joiner",
        approver: "R. Mehta",
      },
      {
        effectiveDate: "20 Feb 2025",
        employeeId: "E-1623",
        department: "Support",
        movement: "Leaver",
        approver: "S. Patel",
      },
      {
        effectiveDate: "12 Mar 2025",
        employeeId: "E-2067",
        department: "Finance",
        movement: "Joiner",
        approver: "R. Mehta",
      },
    ],
  },
  {
    id: "purchase-orders",
    name: "Purchase orders",
    sources: [
      {
        type: "connector",
        id: "src-po-xero",
        connectorType: "CSV export",
        name: "GL_Northwind_US_FY24.csv",
        status: "Connected",
        lastSync: "Yesterday",
      },
    ],
    prompt:
      "Pull all purchase orders raised during FY2024 (1 April 2023 to 31 March 2024), " +
      "including PO number, supplier, order date, line items, and total value. " +
      "Include authorised and billed orders; exclude draft and deleted orders.",
    description: "Authorised and billed purchase orders raised during FY2024.",
    records: "8,902",
    usedIn: "1 sample",
    lastRefreshed: "Yesterday",
    schema: [
      {key: "poNumber", label: "PO number", type: "text", isKey: true},
      {key: "supplier", label: "Supplier", type: "text"},
      {key: "orderDate", label: "Order date", type: "date"},
      {key: "total", label: "Total", type: "currency", numeric: true},
      {key: "status", label: "Status", type: "text"},
    ],
    rows: [
      {poNumber: "PO-48821", supplier: "Contoso Ltd", orderDate: "12 Apr 2023", total: "14,250.00", status: "Billed"},
      {
        poNumber: "PO-48822",
        supplier: "Fabrikam Inc",
        orderDate: "03 May 2023",
        total: "8,940.50",
        status: "Authorised",
      },
      {poNumber: "PO-48910", supplier: "Tailspin Toys", orderDate: "21 Jun 2023", total: "2,300.00", status: "Billed"},
      {
        poNumber: "PO-49003",
        supplier: "Adventure Works",
        orderDate: "09 Sep 2023",
        total: "56,000.00",
        status: "Billed",
      },
      {
        poNumber: "PO-49120",
        supplier: "Northwind Traders",
        orderDate: "14 Jan 2024",
        total: "1,120.00",
        status: "Authorised",
      },
    ],
  },
  {
    id: "user-access-finance-app",
    name: "User access — Finance app",
    sources: [
      {
        type: "file",
        id: "src-access-review",
        label: "Access_Review_Mar25.pdf",
        fileType: "PDF document",
        size: "1.1 MB",
        uploadedBy: "A. Okafor",
        uploadedAt: "31 Mar 2025",
      },
    ],
    prompt:
      "Extract one record per user listed in the entitlement tables of the access-review PDF, " +
      "capturing the review date, user ID, role, access level, and the reviewer. Ignore page " +
      "headers, footers, and summary sections.",
    description: "User accounts and entitlements for the finance application, as at 31 March 2025.",
    records: "542",
    usedIn: "1 sample",
    lastRefreshed: "3 days ago",
    schema: [
      {key: "reviewDate", label: "Review date", type: "date"},
      {key: "userId", label: "User ID", type: "text", isKey: true},
      {key: "role", label: "Role", type: "text"},
      {key: "accessLevel", label: "Access level", type: "text"},
      {key: "reviewer", label: "Reviewer", type: "text"},
    ],
    rows: [
      {reviewDate: "31 Mar 2025", userId: "jsmith", role: "AP Clerk", accessLevel: "Standard", reviewer: "A. Okafor"},
      {
        reviewDate: "31 Mar 2025",
        userId: "alopez",
        role: "Finance Manager",
        accessLevel: "Elevated",
        reviewer: "A. Okafor",
      },
      {reviewDate: "31 Mar 2025", userId: "rkhan", role: "Controller", accessLevel: "Admin", reviewer: "A. Okafor"},
      {reviewDate: "31 Mar 2025", userId: "mchen", role: "AR Clerk", accessLevel: "Standard", reviewer: "A. Okafor"},
      {reviewDate: "31 Mar 2025", userId: "twright", role: "Auditor", accessLevel: "Read-only", reviewer: "A. Okafor"},
    ],
  },
];
